import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { alpha, styled } from "@mui/material/styles";
import { blue, green, grey, orange } from "@mui/material/colors";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import ContentPasteSearchOutlinedIcon from "@mui/icons-material/ContentPasteSearchOutlined";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCart";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ScaleIcon from "@mui/icons-material/Scale";

// API
import { getComplements } from "../../../../api/complement";

// Components
import AddComplementListItem from "./AddComplementListItem";
import ProductComplementListItem from "./ProductComplementListItem";
import LoadingBox from "../../../indicators/LoadingBox";

// Redux actions
import {
  addProductToCartProducts,
  setItem,
} from "../../../../store/actions/newOrderSlice";

// Utilities
import { parseFloatToFixed } from "../../../../extras";
import { formatFinance } from "../../../../utils/formatters";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4 } },
};

const slideUp = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

// Styled components
const RootContainer = styled(motion.div)({
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "column",
  height: "100%",
  minHeight: "650px",
  overflow: "hidden",
});

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: "relative",
  padding: theme.spacing(2, 0),
}));

const ContentCard = styled(motion.div)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  backgroundColor: "#fff",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
  border: `1px solid ${alpha(grey[300], 0.4)}`,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition: "all 0.3s ease",
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${alpha(grey[200], 0.7)}`,
  backgroundColor: alpha(blue[50], 0.5),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: alpha(grey[200], 0.6),
    borderRadius: 10,
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(grey[400], 0.6),
    borderRadius: 10,
    "&:hover": {
      background: alpha(grey[500], 0.7),
    },
  },
}));

const FloatingArrow = styled(motion.div)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: alpha(blue[100], 0.5),
  borderRadius: "50%",
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: blue[700],
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
  zIndex: 5,
}));

const PrimaryButton = styled(motion.button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  backgroundColor: green[600],
  color: "#fff",
  border: "none",
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  cursor: "pointer",
  fontSize: "1rem",
  fontWeight: 600,
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  marginTop: theme.spacing(2),
  "&:hover": {
    backgroundColor: green[700],
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-2px)",
  },
  "&:disabled": {
    backgroundColor: grey[300],
    cursor: "not-allowed",
    transform: "none",
  },
}));

const SecondaryButton = styled(motion.button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  backgroundColor: blue[50],
  color: blue[700],
  border: `1px solid ${blue[200]}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  cursor: "pointer",
  fontSize: "1rem",
  fontWeight: 600,
  transition: "all 0.3s ease",
  marginTop: theme.spacing(2),
  "&:hover": {
    backgroundColor: blue[100],
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  borderRadius: 16,
  backgroundColor: alpha(grey[50], 0.7),
  border: `1px dashed ${alpha(grey[300], 0.7)}`,
  margin: theme.spacing(1),
}));

const WeightInputCard = styled(motion.div)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  backgroundColor: alpha(orange[50], 0.3),
  border: `1px solid ${alpha(orange[200], 0.5)}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

// Utility functions
const formatBrazilianNumber = (value) => {
  if (!value) return "";
  return value.toString().replace(".", ",");
};

const parseBrazilianNumber = (value) => {
  if (!value) return 0;
  return parseFloat(value.replace(",", ".")) || 0;
};

const getWeightLabel = (weight) => {
  if (weight >= 1) return "Kg";
  return "g";
};

const formatWeightDisplay = (weight) => {
  return formatBrazilianNumber(weight.toFixed(3));
};

// Main component
const ProductSpecs = ({ product }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [complements, setComplements] = useState([]);
  const [complementsPrice, setComponentsPrice] = useState(0);
  const [observation, setObservation] = useState(product.observation ?? "");
  const [weightInput, setWeightInput] = useState("");
  const [weightError, setWeightError] = useState("");
  const [currentProduct, setCurrentProduct] = useState(product);

  const isWeightBased = product.unit === "KG";

  useEffect(() => {
    const loadData = async () => {
      try {
        const params = { page: 1, pid: product.product_id };
        const { data } = await getComplements(params);

        const treatedComplements = data.map((complement) => ({
          ...complement,
          title: complement.complement_title,
          price: complement.complement_price,
        }));

        setComplements(treatedComplements);
      } catch (e) {
        console.error("Error loading complements:", e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [product.product_id]);

  useEffect(() => {
    const productComplementsPrice =
      currentProduct.complements?.reduce((acc, currentValue) => {
        return acc + currentValue.price * currentValue.quantity;
      }, 0) || 0;

    setComponentsPrice(productComplementsPrice);

    // Update total price
    const newTotalPrice =
      parseFloat(currentProduct.price || 0) + productComplementsPrice;
    setCurrentProduct((prev) => ({
      ...prev,
      complementsPrice: productComplementsPrice,
      total_price: newTotalPrice,
    }));
  }, [currentProduct.complements, currentProduct.price]);

  const handleWeightChange = (e) => {
    const value = e.target.value;

    // Allow only numbers, comma, and single decimal point
    const validInput = /^\d*,?\d*$/.test(value);

    if (!validInput) return;

    setWeightInput(value);
    setWeightError("");

    const numericValue = parseBrazilianNumber(value);

    if (numericValue > 0) {
      setCurrentProduct((prev) => ({
        ...prev,
        quantity: numericValue,
      }));
    } else if (numericValue === 0 && value !== "") {
      setWeightError("Peso deve ser maior que 0");
    }
  };

  const validateWeight = () => {
    const numericValue = parseBrazilianNumber(weightInput);
    if (numericValue <= 0) {
      setWeightError("Por favor, insira um peso válido");
      return false;
    }
    if (numericValue > 50) {
      setWeightError("Peso máximo permitido é 50kg");
      return false;
    }
    return true;
  };

  const goBack = () => dispatch(setItem(null));

  const handleAddComplement = (complement) => {
    const existingComplementIndex =
      currentProduct.complements?.findIndex((c) => c.id === complement.id) ??
      -1;

    let updatedComplements;
    if (existingComplementIndex !== -1) {
      updatedComplements = [...(currentProduct.complements || [])];
      updatedComplements[existingComplementIndex].quantity += 1;
    } else {
      updatedComplements = [
        ...(currentProduct.complements || []),
        { ...complement, quantity: 1 },
      ];
    }

    setCurrentProduct((prev) => ({
      ...prev,
      complements: updatedComplements,
    }));
  };

  const decreaseComplementQuantity = (complement) => {
    const updatedComplements =
      currentProduct.complements
        ?.map((c) => {
          if (c.id === complement.id) {
            return { ...c, quantity: Math.max(0, c.quantity - 1) };
          }
          return c;
        })
        .filter((c) => c.quantity > 0) || [];

    setCurrentProduct((prev) => ({
      ...prev,
      complements: updatedComplements,
    }));
  };

  const deleteComplement = (complementId) => {
    const updatedComplements =
      currentProduct.complements?.filter((c) => c.id !== complementId) || [];

    setCurrentProduct((prev) => ({
      ...prev,
      complements: updatedComplements,
    }));
  };

  const submitProduct = () => {
    if (isWeightBased && !validateWeight()) {
      return;
    }

    const finalQuantity = isWeightBased ? parseBrazilianNumber(weightInput) : 1;

    // Update the Redux product state with the correct quantity
    dispatch(
      setItem({
        ...currentProduct,
        observation,
        quantity: finalQuantity,
      })
    );

    // Add to cart - NOTE: This will work correctly only if you fix the Redux slice
    // Change `quantity: 1,` to `quantity: state.product.quantity || 1,` in addProductToCartProducts
    dispatch(addProductToCartProducts());
  };

  const currentWeight = parseBrazilianNumber(weightInput);
  const weightLabel = getWeightLabel(currentWeight);
  const isValidWeight = !isWeightBased || currentWeight > 0;

  return (
    <RootContainer initial="hidden" animate="visible" variants={fadeIn}>
      <PageHeader>
        <Typography
          variant="h5"
          align="center"
          sx={{
            fontWeight: 600,
            color: grey[800],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <RestaurantMenuIcon sx={{ color: blue[600], fontSize: 28 }} />
          Personalizando seu Item
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ color: grey[600], mt: 0.5 }}
        >
          {product.title}
        </Typography>
        {isWeightBased && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Chip
              icon={<ScaleIcon />}
              label="Produto vendido por peso"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </PageHeader>

      {/* Weight Input for KG products */}
      {isWeightBased && (
        <WeightInputCard
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScaleIcon sx={{ color: orange[600] }} />
            <Typography
              variant="h6"
              sx={{ color: orange[800], fontWeight: 600 }}
            >
              Informe o Peso Desejado
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Peso"
            variant="outlined"
            value={weightInput}
            onChange={handleWeightChange}
            error={!!weightError}
            helperText={
              weightError || `Formato: use vírgula para decimais (ex: 1,5)`
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: orange[700] }}
                  >
                    {weightLabel}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              ".MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: alpha("#fff", 0.8),
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
              ".MuiInputLabel-root": {
                color: orange[600],
              },
            }}
          />

          {isValidWeight && currentWeight > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                backgroundColor: alpha(green[50], 0.5),
                borderRadius: 1,
                border: `1px solid ${alpha(green[200], 0.5)}`,
              }}
            >
              <Typography variant="body2" sx={{ color: green[800] }}>
                Peso selecionado:{" "}
                <strong>
                  {formatWeightDisplay(currentWeight)} {weightLabel}
                </strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: green[700], fontWeight: 600 }}
              >
                {formatFinance(
                  parseFloatToFixed(product.price * currentWeight)
                )}
              </Typography>
            </Box>
          )}
        </WeightInputCard>
      )}

      <TextField
        fullWidth
        label="Observação (opcional) Ex: Sem cebola, bem passado..."
        variant="outlined"
        multiline
        rows={2}
        value={observation}
        onChange={(e) => setObservation(e.target.value)}
        sx={{
          mb: 3,
          ".MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: alpha("#fff", 0.8),
          },
        }}
      />

      {!isWeightBased && (
        <Grid
          container
          spacing={3}
          sx={{
            flexGrow: 1,
            position: "relative",
            justifyContent: "space-between",
          }}
        >
          {/* Available Complements */}
          <Grid item xs={12} md={5.5} sx={{ height: { md: "500px" } }}>
            <ContentCard variants={slideUp}>
              <SectionHeader>
                <Avatar
                  sx={{ bgcolor: alpha(blue[100], 0.6), color: blue[700] }}
                >
                  <Inventory2OutlinedIcon />
                </Avatar>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={grey[800]}
                >
                  Adicionais Disponíveis
                </Typography>
              </SectionHeader>

              <ScrollableContent>
                {loading ? (
                  <LoadingBox heightMetric="%" />
                ) : complements.length === 0 ? (
                  <EmptyStateContainer>
                    <ContentPasteSearchOutlinedIcon
                      sx={{ fontSize: 40, color: grey[400], mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      color={grey[700]}
                      align="center"
                      sx={{ fontWeight: 500 }}
                    >
                      Nenhum adicional disponível para este item
                    </Typography>
                  </EmptyStateContainer>
                ) : (
                  <AnimatePresence>
                    <motion.div variants={staggerContainer}>
                      {complements.map((complement, index) => (
                        <motion.div
                          key={`${complement.complement}-${index}`}
                          variants={slideUp}
                          initial="hidden"
                          animate="visible"
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <AddComplementListItem
                            complement={complement}
                            addComplement={handleAddComplement}
                            index={index}
                          />
                        </motion.div>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                )}
              </ScrollableContent>
            </ContentCard>
          </Grid>

          {/* Arrow for larger screens */}
          <FloatingArrow
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{
              opacity: [0, 1, 0.5, 1],
              scale: [0.5, 1.1, 0.9, 1],
              x: ["-50%", "-40%", "-60%", "-50%"],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <ChevronRightIcon fontSize="medium" />
          </FloatingArrow>

          {/* Selected Complements */}
          <Grid item xs={12} md={5.5} sx={{ height: { md: "500px" } }}>
            <ContentCard variants={slideUp} transition={{ delay: 0.1 }}>
              <SectionHeader>
                <Avatar
                  sx={{ bgcolor: alpha(green[100], 0.6), color: green[700] }}
                >
                  <ShoppingBasketOutlinedIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color={grey[800]}
                  >
                    Seu Pedido
                  </Typography>
                  <Typography variant="caption" color={grey[600]}>
                    {currentProduct.complements?.length || 0} adicionais
                    selecionados
                  </Typography>
                </Box>
              </SectionHeader>

              <ScrollableContent>
                {!currentProduct?.complements ||
                currentProduct.complements.length === 0 ? (
                  <EmptyStateContainer>
                    <LocalMallIcon
                      sx={{ fontSize: 40, color: grey[400], mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      color={grey[700]}
                      align="center"
                      sx={{ fontWeight: 500 }}
                    >
                      Adicione complementos do painel à esquerda
                    </Typography>
                    <Typography
                      variant="caption"
                      color={grey[500]}
                      align="center"
                      sx={{ mt: 1, maxWidth: 280 }}
                    >
                      Este item não possui nenhum adicional selecionado ainda
                    </Typography>
                  </EmptyStateContainer>
                ) : (
                  <AnimatePresence>
                    <motion.div variants={staggerContainer}>
                      {currentProduct.complements.map((complement, index) => (
                        <motion.div
                          key={`selected-${complement.complement}-${index}`}
                          variants={slideUp}
                          initial="hidden"
                          animate="visible"
                          exit={{ opacity: 0, y: -10 }}
                        >
                          <ProductComplementListItem
                            complement={complement}
                            increaseQuantity={handleAddComplement}
                            decreaseQuantity={decreaseComplementQuantity}
                            remove={deleteComplement}
                            index={index}
                          />
                        </motion.div>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                )}
              </ScrollableContent>
            </ContentCard>
          </Grid>
        </Grid>
      )}

      {/* Action Buttons */}
      <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
        <Box>
          <SecondaryButton onClick={goBack} whileTap={{ scale: 0.98 }}>
            <ArrowBackIcon fontSize="small" />
            Voltar
          </SecondaryButton>
        </Box>
        <Box>
          <PrimaryButton
            onClick={submitProduct}
            disabled={!isValidWeight}
            whileHover={!isValidWeight ? {} : { scale: 1.02 }}
            whileTap={!isValidWeight ? {} : { scale: 0.98 }}
          >
            <AddShoppingCartOutlinedIcon />
            Adicionar ao Carrinho{" "}
            {isWeightBased
              ? formatFinance(
                  parseFloatToFixed(
                    (currentProduct.total_price || currentProduct.price) *
                      (currentWeight || 0)
                  )
                )
              : formatFinance(
                  parseFloatToFixed(
                    currentProduct.total_price || currentProduct.price
                  )
                )}
          </PrimaryButton>
        </Box>
      </Box>
    </RootContainer>
  );
};

export default ProductSpecs;
